import { Component } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Probeert zo veel mogelijk context over de ingelogde gebruiker te verzamelen
 * zodat we een crash snel kunnen koppelen aan een specifieke klant.
 */
const collectUserContext = () => {
  const safeGet = (k) => {
    try { return localStorage.getItem(k); } catch { return null; }
  };
  // We ondersteunen zowel admin/medewerker ('token' / 'user') als klant ('klant_token' / 'klant_user')
  const parse = (raw) => {
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
  };
  const adminUser = parse(safeGet('user'));
  const klantUser = parse(safeGet('klant_user'));
  if (klantUser) {
    return {
      user_type: 'klant',
      user_id: klantUser.id || klantUser.klant_id || klantUser.email || null,
      user_label: klantUser.naam || klantUser.bedrijfsnaam || klantUser.email || null,
    };
  }
  if (adminUser) {
    return {
      user_type: adminUser.role || 'medewerker',
      user_id: adminUser.id || adminUser.email || null,
      user_label: adminUser.naam || adminUser.email || null,
    };
  }
  return { user_type: 'anon', user_id: null, user_label: null };
};

const getAppVersion = () => {
  try { return localStorage.getItem('waszbase_app_version') || '?'; } catch { return '?'; }
};

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, showDetails: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this._errorInfo = errorInfo;
    try {
      // eslint-disable-next-line no-console
      console.error('WaszBase Error:', error, errorInfo);
      if (!API_URL) return;
      const ctx = collectUserContext();
      const payload = {
        message: String(error?.message || 'Onbekende fout').slice(0, 8000),
        stack: String(error?.stack || '').slice(0, 8000),
        component_stack: String(errorInfo?.componentStack || '').slice(0, 8000),
        url: window.location.href,
        user_agent: navigator.userAgent,
        app_version: getAppVersion(),
        user_id: ctx.user_id,
        user_type: ctx.user_type,
        user_label: ctx.user_label,
      };
      // keepalive zorgt dat de POST ook aankomt als de pagina direct hard reload
      fetch(`${API_URL}/api/client-errors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    } catch { /* noop */ }
  }

  handleHardReload = async () => {
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map(r => r.unregister()));
      }
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map(k => caches.delete(k)));
      }
    } catch { /* noop */ }
    window.location.reload(true);
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const errorMessage = this.state.error?.message || 'Onbekende fout';
    const errorStack = this.state.error?.stack || '';
    const cachedVersion = getAppVersion();

    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #1e3a5f 0%, #0a1628 100%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        color: 'white', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '20px', textAlign: 'center',
      }}>
        <img src="/wasz-logo.png" alt="WaszBase" style={{ width: 120, marginBottom: 24 }} />
        <h2 style={{ marginBottom: 12, fontSize: 22 }}>Er is iets misgegaan</h2>
        <p style={{ color: 'rgba(255,255,255,0.75)', marginBottom: 20, maxWidth: 440, lineHeight: 1.5 }}>
          Er is een onverwachte fout opgetreden. De fout is automatisch gemeld.
          Probeer eerst opnieuw te laden. Blijft het gebeuren, klik dan op &quot;Cache wissen &amp; herstarten&quot;.
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 24 }}>
          <button
            onClick={() => window.location.reload()}
            style={{ background: '#3b82f6', color: 'white', border: 'none', padding: '12px 24px', borderRadius: 8, fontSize: 15, cursor: 'pointer', fontWeight: 600 }}
            data-testid="eb-reload"
          >
            Opnieuw proberen
          </button>
          <button
            onClick={this.handleHardReload}
            style={{ background: '#dc2626', color: 'white', border: 'none', padding: '12px 24px', borderRadius: 8, fontSize: 15, cursor: 'pointer', fontWeight: 600 }}
            data-testid="eb-hard-reload"
          >
            Cache wissen &amp; herstarten
          </button>
        </div>
        <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12 }}>
          App-versie: <strong>v{cachedVersion}</strong>
        </div>
        <button
          onClick={() => this.setState({ showDetails: !this.state.showDetails })}
          style={{ marginTop: 16, background: 'transparent', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.2)', padding: '6px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}
          data-testid="eb-toggle-details"
        >
          {this.state.showDetails ? 'Verberg technische details' : 'Toon technische details'}
        </button>
        {this.state.showDetails && (
          <div style={{ marginTop: 14, maxWidth: 640, width: '100%', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: 14, textAlign: 'left', fontFamily: 'monospace', fontSize: 11, color: 'rgba(255,255,255,0.8)', overflow: 'auto', maxHeight: 260 }}>
            <div style={{ color: '#fca5a5', marginBottom: 8, fontWeight: 'bold' }}>{errorMessage}</div>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{errorStack}</pre>
            {this._errorInfo?.componentStack && (
              <pre style={{ margin: '12px 0 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'rgba(255,255,255,0.5)' }}>
                {this._errorInfo.componentStack}
              </pre>
            )}
          </div>
        )}
      </div>
    );
  }
}

export default ErrorBoundary;
